import { useTheme } from "@mui/material/styles";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import { forwardRef, useMemo } from "react";
import { getM3Colors } from "../../theme/style/m3Palette";

/**
 * Material 3 styled DatePicker component.
 * Uses Poppins typography and Arivu M3 design tokens.
 */
const M3DatePicker = forwardRef(function M3DatePicker(
  {
    value,
    onChange,
    label,
    placeholder = "YYYY-MM-DD",
    format = "YYYY-MM-DD",
    views = ["year", "month", "day"],
    disabled = false,
    readOnly = false,
    size = "small",
    open,
    onOpen,
    onClose,
    slotProps = {},
    sx = {},
    autoFocus = false,
    fullWidth = true,
    minDate,
    maxDate,
    ...rest
  },
  ref
) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const m3 = getM3Colors(isDark);

  // Normalize incoming value to a dayjs object or null
  const parsedValue = useMemo(() => {
    if (!value || value === "N/A" || value === "None" || value === "-") return null;
    if (dayjs.isDayjs(value)) return value.isValid() ? value : null;
    const cleanStr = String(value).replace(/\s+00:00:00.*$/, "").trim();
    const d = dayjs(cleanStr);
    return d.isValid() ? d : null;
  }, [value]);

  const handleChange = (newDate) => {
    if (!onChange) return;
    if (newDate && dayjs.isDayjs(newDate) && newDate.isValid()) {
      onChange(newDate, newDate.format(format));
    } else {
      onChange(null, "");
    }
  };

  const defaultSx = {
    width: fullWidth ? "100%" : "auto",
    "& .MuiInputBase-root": {
      fontFamily: "Poppins, sans-serif",
      fontSize: "13px",
      borderRadius: "8px",
      backgroundColor: isDark ? "rgba(255, 255, 255, 0.04)" : "rgba(0, 0, 0, 0.02)",
      color: m3.onSurface,
      transition: "all 0.2s ease",
      "& fieldset": {
        borderColor: m3.outlineVariant,
        borderRadius: "8px",
        transition: "border-color 0.2s ease",
      },
      "&:hover fieldset": {
        borderColor: m3.primary,
      },
      "&.Mui-focused fieldset": {
        borderColor: m3.primary,
        borderWidth: "2px",
      },
      "& input": {
        padding: size === "small" ? "7px 10px" : "10px 14px",
        fontFamily: "Poppins, sans-serif",
        fontSize: "13px",
        color: m3.onSurface,
        "&::placeholder": {
          color: m3.outline,
          opacity: 0.8,
        },
      },
    },
    "& .MuiFormLabel-root": {
      fontFamily: "Poppins, sans-serif",
      fontSize: "13px",
      color: m3.onSurfaceVariant,
      "&.Mui-focused": {
        color: m3.primary,
      },
    },
    "& .MuiIconButton-root": {
      color: m3.onSurfaceVariant,
      padding: "4px",
      "&:hover": {
        color: m3.primary,
        backgroundColor: isDark ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.05)",
      },
    },
    ...sx,
  };

  const defaultPopperSx = {
    zIndex: 13000,
    "& .MuiPaper-root": {
      borderRadius: "16px",
      backgroundColor: isDark ? "#1e293b" : "#ffffff",
      color: isDark ? "#f8fafc" : "#0f172a",
      border: `1px solid ${isDark ? "rgba(255, 255, 255, 0.12)" : "#e2e8f0"}`,
      boxShadow: isDark
        ? "0 12px 32px rgba(0, 0, 0, 0.6)"
        : "0 10px 30px rgba(0, 0, 0, 0.12)",
      fontFamily: "Poppins, sans-serif",
      overflow: "hidden",
      "& *": {
        fontFamily: "Poppins, sans-serif !important",
      },
      // Calendar Header
      "& .MuiPickersCalendarHeader-root": {
        paddingTop: "12px",
        paddingBottom: "8px",
        "& .MuiPickersCalendarHeader-label": {
          fontWeight: 600,
          fontSize: "14px",
          color: isDark ? "#ffffff" : "#0f172a",
        },
        "& .MuiPickersArrowSwitcher-root .MuiIconButton-root": {
          color: isDark ? "#94a3b8" : "#64748b",
          "&:hover": {
            backgroundColor: isDark ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.05)",
            color: "#16a34a",
          },
        },
      },
      // Weekday labels
      "& .MuiDayCalendar-weekDayLabel": {
        color: isDark ? "#94a3b8" : "#64748b",
        fontWeight: 600,
        fontSize: "12px",
      },
      // Days
      "& .MuiPickersDay-root": {
        fontFamily: "Poppins, sans-serif",
        fontSize: "12.5px",
        color: isDark ? "#f1f5f9" : "#1e293b",
        borderRadius: "100px",
        margin: "1px",
        transition: "all 0.15s ease",
        "&:hover": {
          backgroundColor: isDark ? "rgba(34, 197, 94, 0.18)" : "rgba(22, 163, 74, 0.12)",
          color: isDark ? "#4ade80" : "#16a34a",
        },
        "&.Mui-selected": {
          backgroundColor: "#16a34a !important",
          color: "#ffffff !important",
          fontWeight: 600,
          boxShadow: isDark
            ? "0 2px 8px rgba(0,0,0,0.5)"
            : "0 2px 8px rgba(22, 163, 74, 0.35)",
        },
        "&.MuiPickersDay-today": {
          borderColor: "#16a34a",
          fontWeight: 600,
          color: isDark ? "#4ade80" : "#16a34a",
        },
        "&.Mui-disabled": {
          opacity: 0.35,
        },
      },
      // Month / Year views
      "& .MuiYearCalendar-root .MuiPickersYear-yearButton": {
        fontFamily: "Poppins, sans-serif",
        fontSize: "13px",
        borderRadius: "8px",
        "&:hover": {
          backgroundColor: isDark ? "rgba(34, 197, 94, 0.18)" : "rgba(22, 163, 74, 0.12)",
        },
        "&.Mui-selected": {
          backgroundColor: "#16a34a !important",
          color: "#ffffff !important",
          fontWeight: 600,
        },
      },
      "& .MuiMonthCalendar-root .MuiPickersMonth-monthButton": {
        fontFamily: "Poppins, sans-serif",
        fontSize: "13px",
        borderRadius: "8px",
        "&:hover": {
          backgroundColor: isDark ? "rgba(34, 197, 94, 0.18)" : "rgba(22, 163, 74, 0.12)",
        },
        "&.Mui-selected": {
          backgroundColor: "#16a34a !important",
          color: "#ffffff !important",
          fontWeight: 600,
        },
      },
    },
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DatePicker
        ref={ref}
        value={parsedValue}
        onChange={handleChange}
        label={label}
        format={format}
        views={views}
        disabled={disabled}
        readOnly={readOnly}
        open={open}
        onOpen={onOpen}
        onClose={onClose}
        minDate={minDate}
        maxDate={maxDate}
        autoFocus={autoFocus}
        slotProps={{
          textField: {
            size,
            placeholder,
            inputProps: {
              autoFocus,
            },
            ...slotProps.textField,
          },
          popper: {
            sx: defaultPopperSx,
            ...slotProps.popper,
          },
          ...slotProps,
        }}
        sx={defaultSx}
        {...rest}
      />
    </LocalizationProvider>
  );
});

export default M3DatePicker;
